import React, { useState } from "react";
import { ArrowRight, Mail, MapPin, Phone, Printer, ExternalLink, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PageHero, Reveal, Eyebrow, SectionHeading } from "../components/Shared";
import { IMAGES, HEADQUARTERS, OFFICES, ORG_TYPES, INTERESTS } from "../mock";
import { createInquiry, errorMessage } from "../lib/api";

const initial = { name: "", organization: "", email: "", phone: "", orgType: "", interests: [], message: "" };

const ContactForm = () => {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleInterest = (i) => set("interests", form.interests.includes(i) ? form.interests.filter((x) => x !== i) : [...form.interests, i]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.organization.trim()) e.organization = "Please enter your organization.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email.";
    if (!form.message.trim()) e.message = "Tell us a little about what you need.";
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setSubmitting(true);
    try {
      const rec = await createInquiry(form);
      setDone(rec);
      toast.success("Thanks! Your message has been received.", { description: "A member of our senior management team will follow up shortly." });
    } catch (err) {
      toast.error("We couldn't send your message.", { description: errorMessage(err) });
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="rounded-3xl border border-brand-100 bg-white p-8 lg:p-10" data-testid="contact-success">
        <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-700"><CheckCircle2 size={28} /></span>
        <h3 className="mt-6 font-serif text-3xl text-ink-900">Thank you, {done.name.split(" ")[0]}.</h3>
        <p className="mt-3 text-ink-600">We've received your message about <span className="font-medium text-ink-900">{done.organization}</span>. A member of our senior management team will personally review it and follow up at <span className="font-medium text-ink-900">{done.email}</span>.</p>
        <Button onClick={() => { setForm(initial); setDone(null); }} variant="outline" className="btn-pill mt-8 border-brand-200 text-brand-700 hover:bg-brand-50" data-testid="contact-reset">
          Send another message
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate className="rounded-3xl border border-brand-100 bg-white p-6 lg:p-10" data-testid="contact-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="name">Your name *</Label>
          <Input id="name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Smith" className="h-11 rounded-xl" data-testid="input-name" />
          {errors.name && <p className="text-xs text-red-600">{errors.name}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="organization">Organization *</Label>
          <Input id="organization" value={form.organization} onChange={(e) => set("organization", e.target.value)} placeholder="Seacoast Community Center" className="h-11 rounded-xl" data-testid="input-organization" />
          {errors.organization && <p className="text-xs text-red-600">{errors.organization}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@organization.org" className="h-11 rounded-xl" data-testid="input-email" />
          {errors.email && <p className="text-xs text-red-600">{errors.email}</p>}
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Phone</Label>
          <Input id="phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(603) 555-0100" className="h-11 rounded-xl" data-testid="input-phone" />
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label>Type of organization</Label>
          <Select value={form.orgType} onValueChange={(v) => set("orgType", v)}>
            <SelectTrigger className="h-11 rounded-xl" data-testid="select-org-type"><SelectValue placeholder="Select one" /></SelectTrigger>
            <SelectContent>
              {ORG_TYPES.map((o) => (<SelectItem key={o} value={o}>{o}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-3 sm:col-span-2">
          <Label>I'm interested in</Label>
          <div className="grid gap-2.5 sm:grid-cols-2">
            {INTERESTS.map((i) => (
              <label key={i} className="flex cursor-pointer items-center gap-3 rounded-xl border border-brand-100 px-4 py-3 text-sm text-ink-700 transition-colors hover:bg-brand-50 has-[[data-state=checked]]:border-brand-500 has-[[data-state=checked]]:bg-brand-50">
                <Checkbox checked={form.interests.includes(i)} onCheckedChange={() => toggleInterest(i)} data-testid={`interest-${i.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
                {i}
              </label>
            ))}
          </div>
        </div>
        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="message">How can we help? *</Label>
          <Textarea id="message" rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us about your organization and what you're looking for." className="rounded-xl" data-testid="input-message" />
          {errors.message && <p className="text-xs text-red-600">{errors.message}</p>}
        </div>
      </div>
      <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-ink-500">We respond to every inquiry. No sales pressure, ever.</p>
        <Button type="submit" disabled={submitting} className="btn-pill h-12 bg-brand-600 px-8 text-white hover:bg-brand-700" data-testid="contact-submit">
          {submitting ? "Sending..." : "Send message"} {!submitting && <ArrowRight size={16} className="ml-2" />}
        </Button>
      </div>
    </form>
  );
};

const OfficeCard = ({ o, i }) => (
  <Reveal delay={i * 50} className="card-lift rounded-2xl border border-brand-100 bg-white p-6" data-testid={`office-${i}`}>
    <div className="flex items-start justify-between">
      <h3 className="font-serif text-lg text-ink-900">{o.name}</h3>
      <span className="rounded-full bg-brand-50 px-2.5 py-1 font-mono text-[11px] tracking-widest text-brand-700">{o.state}</span>
    </div>
    <p className="mt-3 text-sm leading-relaxed text-ink-600">{o.address[0]}<br />{o.address[1]}</p>
    <div className="mt-4 space-y-1.5 text-sm">
      <a href={`tel:${o.phone.replace(/\D/g, "")}`} className="flex items-center gap-2 text-ink-800 transition-colors hover:text-brand-700"><Phone size={14} className="text-brand-600" />{o.phone}{o.alt ? ` or ${o.alt}` : ""}</a>
      {o.fax && <p className="flex items-center gap-2 text-ink-600"><Printer size={14} className="text-brand-600" />Fax {o.fax}</p>}
    </div>
    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(o.address.join(", "))}`} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 hover:text-brand-900">
      Directions <ExternalLink size={13} />
    </a>
  </Reveal>
);

const Contact = () => (
  <>
    <PageHero compact eyebrow="Contact us" title="Let's talk about your nonprofit." intro="A member of our senior management team will personally review your request and follow up to discuss next steps." image={IMAGES.handshake} />
    <section className="section" data-testid="contact-content">
      <div className="container-x grid gap-12 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal><ContactForm /></Reveal>
        </div>
        <div className="lg:col-span-5">
          <Reveal delay={100} className="rounded-3xl bg-brand-700 p-8 text-white" data-testid="hq-card">
            <Eyebrow light>Great North Advantage</Eyebrow>
            <h3 className="mt-3 font-serif text-2xl">{HEADQUARTERS.name}</h3>
            <p className="mt-4 flex items-start gap-3 text-sand-100"><MapPin size={18} className="mt-0.5 shrink-0 text-gold-200" />{HEADQUARTERS.address[0]}, {HEADQUARTERS.address[1]}</p>
            <a href={`tel:${HEADQUARTERS.phone.replace(/\D/g, "")}`} className="mt-3 flex items-center gap-3 text-sand-100 transition-colors hover:text-white"><Phone size={18} className="shrink-0 text-gold-200" />{HEADQUARTERS.phone}</a>
            <p className="ml-8 text-sm text-sand-300">{HEADQUARTERS.tollFree} (Outside New Hampshire)</p>
            <p className="mt-3 flex items-center gap-3 text-sand-100"><Printer size={18} className="shrink-0 text-gold-200" />Fax {HEADQUARTERS.fax}</p>
            <p className="mt-3 flex items-center gap-3 text-sand-100"><Mail size={18} className="shrink-0 text-gold-200" />Use the form to reach our team</p>
            <a href={HEADQUARTERS.directions} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-gold-200 transition-colors hover:text-white">
              Get directions <ExternalLink size={14} />
            </a>
          </Reveal>
          <Reveal delay={160} className="mt-6 overflow-hidden rounded-3xl border border-brand-100">
            <iframe
              title="Great North Advantage headquarters map"
              src="https://www.google.com/maps?q=3+Holland+Way,+Exeter,+NH+03833&output=embed"
              className="h-64 w-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </Reveal>
        </div>
      </div>
    </section>
    <section className="section bg-white" data-testid="offices-section">
      <div className="container-x">
        <SectionHeading eyebrow="Regional offices" title="Nine offices across New England." intro="Local presence in each marketplace has been a founding principle since 1990." />
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {OFFICES.map((o, i) => (<OfficeCard key={o.name} o={o} i={i} />))}
        </div>
      </div>
    </section>
  </>
);

export default Contact;
